import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { IoIosSearch } from "react-icons/io";
import {
  X,
  User,
  Mail,
  Calendar,
  FileText,
  Download,
  Send,
  MessageSquare,
  Maximize2,
  AlertCircle,
  Check,
  CheckCircle2,
  Trash2,
  Wrench,
  LayoutGrid,
  Archive,
} from "lucide-react";

import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch } from '../../HttpClient';

import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";

// Catégories du type "informatique" uniquement (Ticketing.categorie, enum
// CategorieTicket côté Prisma) — voir claude-design/tickets.jsx (TK_CATS).
const CATEGORIES = [
  { value: "IMPRESSION", label: "Impression", color: "#2B5DA8", bg: "#E8F0FB" },
  { value: "FENETRE", label: "Fenêtre bloquée", color: "#8A5A00", bg: "#FFF4DA" },
  { value: "ACD", label: "Connexion logiciels ACD", color: "#6E1A36", bg: "#FBE8EE" },
  { value: "MATERIEL", label: "Matériel", color: "#1F6B5C", bg: "#E0F2EE" },
  { value: "AUTRES", label: "Autres", color: "#5F5A5C", bg: "#F1EEEF" },
];

// Délai (jours depuis la création) au-delà duquel un ticket non clôturé est
// considéré "en retard" — même règle que claude-design/tickets.jsx (TK_SLA).
const SLA_JOURS = { Urgent: 1, Classique: 3 };

function getCategorie(value) {
  return CATEGORIES.find((c) => c.value === value);
}

function CategorieChip({ value }) {
  const c = getCategorie(value);
  if (!c) return <span className="text-[10.5px] text-slate-300">À classer</span>;
  return (
    <span
      className="inline-flex items-center h-5 px-2 rounded-full text-[10.5px] font-semibold whitespace-nowrap"
      style={{ color: c.color, background: c.bg }}
    >
      {c.label}
    </span>
  );
}

function debutJourLocal(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dateLocaleStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function joursDepuis(dateIso, today) {
  return Math.round((today - debutJourLocal(new Date(dateIso))) / 86400000);
}

function estEnRetard(ticket, today) {
  if (ticket.status === "Terminé" || ticket.status === "Archiver") return false;
  const seuil = SLA_JOURS[ticket.priority] || 3;
  return joursDepuis(ticket.createdAt, today) > seuil;
}

function libelleJour(jourStr, todayStr, hierStr) {
  const d = new Date(jourStr + "T00:00:00");
  let long = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  long = long.charAt(0).toUpperCase() + long.slice(1);
  const rel = jourStr === todayStr ? "AUJOURD'HUI" : jourStr === hierStr ? "HIER" : null;
  return { rel, long };
}

function formatHeure(iso) {
  return new Date(iso)
    .toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    .replace(":", "h");
}

function StatutPill({ ticket, today }) {
  if (ticket.status === "Terminé") {
    return (
      <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10.5px] font-semibold bg-emerald-50 text-emerald-700 whitespace-nowrap">
        <Check className="w-2.5 h-2.5" />
        Clôturé
      </span>
    );
  }
  if (ticket.status === "Archiver") {
    return (
      <span className="inline-flex items-center h-5 px-2 rounded-full text-[10.5px] font-semibold bg-slate-100 text-slate-500 whitespace-nowrap">
        Archivé
      </span>
    );
  }
  if (estEnRetard(ticket, today)) {
    return (
      <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10.5px] font-semibold bg-red-50 text-red-600 whitespace-nowrap">
        <AlertCircle className="w-2.5 h-2.5" />
        En retard · {joursDepuis(ticket.createdAt, today)} j
      </span>
    );
  }
  return (
    <span className="inline-flex items-center h-5 px-2 rounded-full text-[10.5px] font-semibold bg-blue-50 text-blue-700 whitespace-nowrap">
      En cours
    </span>
  );
}

function estTypeInformatique(type) {
  return type === "informatique";
}

// Overlay de confirmation — clôture (vert : action positive, "terminer").
function ConfirmClotureModal({ ticket, onClose, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
          <span className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </span>
          <h3 className="text-sm font-semibold text-gray-900 m-0">Clôturer ce ticket ?</h3>
          <p className="text-xs text-gray-500 mt-2 mb-0">
            Voulez-vous marquer <strong className="text-gray-700">« {ticket.title} »</strong> comme
            terminé ? L'utilisateur en sera notifié par mail.
          </p>
        </div>
        <div className="px-6 py-4 bg-emerald-50 border-t border-emerald-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
            Clôturer
          </button>
        </div>
      </div>
    </div>
  );
}

// Overlay de confirmation — suppression (rouge : action destructrice).
function ConfirmDeleteModal({ ticket, onClose, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
          <span className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <Trash2 className="w-6 h-6 text-red-600" />
          </span>
          <h3 className="text-sm font-semibold text-gray-900 m-0">Supprimer ce ticket ?</h3>
          <p className="text-xs text-gray-500 mt-2 mb-0">
            Voulez-vous supprimer <strong className="text-gray-700">« {ticket.title} »</strong> ?
            Cette action est irréversible.
          </p>
        </div>
        <div className="px-6 py-4 bg-red-50 border-t border-red-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-gray-600 hover:bg-slate-50 text-xs font-medium transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

function TicketingAll() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentaireInput, setCommentaireInput] = useState("");
  const [isSendingComment, setIsSendingComment] = useState(false);

  // Onglets / filtres
  const [tab, setTab] = useState("informatique"); // "informatique" | "myalfa"
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous"); // tous | encours | retard | clotures
  const [categoryFilter, setCategoryFilter] = useState("");

  // 👉 nouvel état pour l’overlay détails
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showImageFullscreen, setShowImageFullscreen] = useState(false);

  const [showArchives, setShowArchives] = useState(false);

  // Overlays de confirmation dédiés (clôture / suppression) — l'action
  // elle-même est optimiste (voir plus bas) : ces overlays ne bloquent que
  // la décision, jamais l'attente réseau.
  const [confirmCloture, setConfirmCloture] = useState(null); // ticket en attente
  const [confirmDelete, setConfirmDelete] = useState(null); // ticket en attente

  const { utilisateur } = useEtatGlobal();

  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);

  const today = useMemo(() => debutJourLocal(new Date()), []);
  const todayStr = dateLocaleStr(today);
  const hierStr = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    return dateLocaleStr(d);
  }, [today]);

  // `silencieux: true` — resynchronisation en arrière-plan (ex. après l'ajout
  // d'un commentaire) : ne touche pas `loading`, donc le tableau ne
  // reskeletonise jamais une fois déjà affiché.
  const fetchTickets = ({ silencieux = false } = {}) => {
    if (!silencieux) setLoading(true);
    authFetch("https://backend-myalfa.vercel.app/api/ticketing")
      .then((res) => res.json())
      .then((data) => {
        setTickets(data);
        if (!silencieux) setLoading(false);
      })
      .catch((err) => {
        console.error("Erreur lors de la récupération des tickets :", err);
        if (!silencieux) setLoading(false);
      });
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Changer d'onglet remet le filtre de catégorie à zéro (il n'a de sens que
  // pour "informatique").
  useEffect(() => {
    setCategoryFilter("");
  }, [tab]);

  const inTab = useMemo(
    () =>
      tickets.filter((t) =>
        tab === "informatique" ? estTypeInformatique(t.type) : !estTypeInformatique(t.type)
      ),
    [tickets, tab]
  );

  const pool = useMemo(
    () =>
      inTab.filter((t) => (showArchives ? t.status === "Archiver" : t.status !== "Archiver")),
    [inTab, showArchives]
  );

  const statusCounts = useMemo(
    () => ({
      tous: pool.length,
      encours: pool.filter((t) => t.status !== "Terminé" && !estEnRetard(t, today)).length,
      retard: pool.filter((t) => estEnRetard(t, today)).length,
      clotures: pool.filter((t) => t.status === "Terminé").length,
    }),
    [pool, today]
  );

  const categoryCounts = useMemo(() => {
    const dict = {};
    CATEGORIES.forEach((c) => {
      dict[c.value] = pool.filter((t) => t.categorie === c.value).length;
    });
    return dict;
  }, [pool]);

  // Badges des onglets (nombre ouverts / en retard) — sur TOUS les tickets
  // non archivés, indépendamment de l'onglet actuellement affiché.
  const compteOuverts = (type) =>
    tickets.filter(
      (t) =>
        t.status !== "Archiver" &&
        t.status !== "Terminé" &&
        (type === "informatique" ? estTypeInformatique(t.type) : !estTypeInformatique(t.type))
    ).length;
  const compteRetard = (type) =>
    tickets.filter(
      (t) =>
        t.status !== "Archiver" &&
        estEnRetard(t, today) &&
        (type === "informatique" ? estTypeInformatique(t.type) : !estTypeInformatique(t.type))
    ).length;

  const listeFiltree = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return pool
      .filter((t) => {
        const matchSearch =
          !q ||
          `${t.nomUser || ""} ${t.title || ""} ${t.description || ""}`.toLowerCase().includes(q);
        const matchStatus =
          statusFilter === "tous"
            ? true
            : statusFilter === "retard"
              ? estEnRetard(t, today)
              : statusFilter === "clotures"
                ? t.status === "Terminé"
                : t.status !== "Terminé" && !estEnRetard(t, today);
        const matchCategorie =
          tab !== "informatique" || !categoryFilter || t.categorie === categoryFilter;
        return matchSearch && matchStatus && matchCategorie;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [pool, searchTerm, statusFilter, categoryFilter, tab, today]);

  const groupes = useMemo(() => {
    const out = [];
    listeFiltree.forEach((t) => {
      const jour = dateLocaleStr(new Date(t.createdAt));
      const dernier = out[out.length - 1];
      if (dernier && dernier.jour === jour) dernier.items.push(t);
      else out.push({ jour, items: [t] });
    });
    return out;
  }, [listeFiltree]);

  const isInfo = tab === "informatique";
  const nbColonnes = isInfo ? 8 : 7;

  // Clôture, suppression, archivage/désarchivage : optimistes — le tableau
  // se met à jour immédiatement, un toast chargement → confirmation informe
  // du résultat réel en arrière-plan ; en cas d'échec, on revient à l'état
  // précédent avec un toast d'erreur.
  const handleValidate = (ticket) => {
    setConfirmCloture(null);
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, status: "Terminé" } : t)));
    const toast = showStatusToast({
      status: "loading",
      titre: "Clôture en cours...",
      message: `"${ticket.title}"`,
    });
    (async () => {
      try {
        const response = await authFetch(
          `https://backend-myalfa.vercel.app/api/ticketing/${ticket.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "Terminé" }),
          }
        );
        if (!response.ok) throw new Error("Erreur lors de la clôture du ticket");

        await authFetch("https://backend-myalfa.vercel.app/api/mail-ticket-validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: ticket.title,
            type: ticket.type,
            description: ticket.description,
            nomUser: ticket.nomUser,
            email: ticket.email,
          }),
        });

        toast.update({
          status: "success",
          titre: "Ticket clôturé",
          message: `"${ticket.title}" a été clôturé.`,
        });
      } catch (error) {
        console.error(error);
        setTickets((prev) =>
          prev.map((t) => (t.id === ticket.id ? { ...t, status: ticket.status } : t))
        );
        toast.update({
          status: "error",
          titre: "Erreur",
          message: "La clôture n'a pas pu être enregistrée.",
        });
      }
    })();
  };

  const handleDelete = (ticket) => {
    setConfirmDelete(null);
    setTickets((prev) => prev.filter((t) => t.id !== ticket.id));
    const toast = showStatusToast({
      status: "loading",
      titre: "Suppression en cours...",
      message: `"${ticket.title}"`,
    });
    (async () => {
      try {
        const response = await authFetch(
          `https://backend-myalfa.vercel.app/api/ticketing/${ticket.id}`,
          { method: "DELETE" }
        );
        if (!response.ok) throw new Error("Erreur lors de la suppression du ticket");
        toast.update({
          status: "success",
          titre: "Ticket supprimé",
          message: `"${ticket.title}" a été supprimé.`,
        });
      } catch (error) {
        console.error(error);
        setTickets((prev) => [...prev, ticket]);
        toast.update({
          status: "error",
          titre: "Erreur",
          message: "La suppression n'a pas pu être enregistrée.",
        });
      }
    })();
  };

  if (loading) {
    return (
      <div className="Portefeuille-container">
        <p className="text-xs text-gray-400 mb-1">Acceuil &gt; Ticketing</p>
        <h2 className="text-xl font-bold text-gray-800 mb-1">Suivi des tickets</h2>
        <p className="text-xs text-gray-400 mb-4">Demandes de support des collaborateurs du cabinet</p>

        <div className="flex items-center gap-4 border-b border-slate-200 mb-4 pb-2.5">
          <div className="h-5 w-28 rounded bg-slate-200 animate-pulse" />
          <div className="h-5 w-20 rounded bg-slate-100 animate-pulse" />
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="h-9 w-64 rounded-xl bg-slate-200 animate-pulse" />
          <div className="h-8 w-72 rounded-lg bg-slate-100 animate-pulse" />
          <span className="flex-1" />
          <div className="h-9 w-24 rounded-lg bg-slate-100 animate-pulse" />
        </div>

        <div className="w-full overflow-x-auto">
          <table
            className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
            style={{ fontSize: "0.68rem" }}
          >
            <thead>
              <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                {["Titre", "Catégorie", "Description", "Utilisateur", "Statut", "Priorité", "Heure", ""].map(
                  (col, i) => (
                    <th key={i} className="px-3 py-2.5 text-left">
                      <div
                        className="h-3 rounded bg-[#ffffff33] animate-pulse"
                        style={{ width: col ? "70%" : "40px" }}
                      />
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="bg-white">
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-32" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-4 w-20 rounded-full bg-slate-100 animate-pulse" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100 animate-pulse w-40" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100 animate-pulse w-24" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-4 w-16 rounded-full bg-slate-100 animate-pulse" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100 animate-pulse w-12" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100 animate-pulse w-10" />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="h-6 rounded-full bg-slate-100 animate-pulse w-16 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const handleAddComment = async () => {
    if (!commentaireInput.trim()) return;

    setIsSendingComment(true);
    try {
      await authFetch("https://backend-myalfa.vercel.app/api/commentairesTicket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nomComplet: `${utilisateur.lastName} ${utilisateur.name}`,
          commentaire: commentaireInput,
          ticketingId: selectedTicket.id,
        }),
      });

      setCommentaireInput("");
    } catch (error) {
      console.error("Erreur en ajoutant le commentaire :", error);
    } finally {
      setIsSendingComment(false);
      fetchTickets({ silencieux: true });
      setShowOverlay(false);
      setShowImageFullscreen(false);
    }
  };

  // ARCHIVAGE — optimiste, comme handleValidate/handleDelete ci-dessus.
  const handleArchive = async (ticket) => {
    const confirmArchive = await dialog.confirm("Voulez-vous archiver ce ticket ?");
    if (!confirmArchive) return;

    const previousStatus = ticket.status;
    setTickets((prev) =>
      prev.map((t) => (t.id === ticket.id ? { ...t, lastStatus: previousStatus, status: "Archiver" } : t))
    );
    const toast = showStatusToast({
      status: "loading",
      titre: "Archivage en cours...",
      message: `"${ticket.title}"`,
    });
    try {
      const response = await authFetch(`https://backend-myalfa.vercel.app/api/ticketing/${ticket.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "Archiver", lastStatus: previousStatus }),
        }
      );
      if (!response.ok) throw new Error("Erreur lors de l'archivage");
      toast.update({ status: "success", titre: "Ticket archivé", message: `"${ticket.title}" a été archivé.` });
    } catch (err) {
      console.error("Archivage échoué :", err);
      setTickets((prev) =>
        prev.map((t) => (t.id === ticket.id ? { ...t, status: previousStatus, lastStatus: undefined } : t))
      );
      toast.update({ status: "error", titre: "Erreur", message: "L'archivage n'a pas pu être enregistré." });
    }
  };

  const handleDesArchive = async (ticket) => {
    const confirmUnarchive = await dialog.confirm("Voulez-vous désarchiver ce ticket ?");
    if (!confirmUnarchive) return;

    const restoredStatus = ticket.lastStatus || "En cours";
    setTickets((prev) =>
      prev.map((t) => (t.id === ticket.id ? { ...t, status: restoredStatus, lastStatus: undefined } : t))
    );
    const toast = showStatusToast({
      status: "loading",
      titre: "Restauration en cours...",
      message: `"${ticket.title}"`,
    });
    try {
      const response = await authFetch(`https://backend-myalfa.vercel.app/api/ticketing/${ticket.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: restoredStatus }),
        }
      );
      if (!response.ok) throw new Error("Erreur lors du désarchivage");
      toast.update({ status: "success", titre: "Ticket restauré", message: `"${ticket.title}" a été restauré.` });
    } catch (err) {
      console.error("Désarchivage échoué :", err);
      setTickets((prev) =>
        prev.map((t) => (t.id === ticket.id ? { ...t, status: "Archiver", lastStatus: restoredStatus } : t))
      );
      toast.update({ status: "error", titre: "Erreur", message: "La restauration n'a pas pu être enregistrée." });
    }
  };

  return (
    <div className="Portefeuille-container">
      <p className="text-xs text-gray-400 mb-1">Acceuil &gt; Ticketing</p>
      <h2 className="text-xl font-bold text-gray-800 mb-1">Suivi des tickets</h2>
      <p className="text-xs text-gray-400 mb-4">Demandes de support des collaborateurs du cabinet</p>

      {/* Onglets Informatique / MyALFA */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {[
          { key: "informatique", label: "Informatique", Icon: Wrench },
          { key: "myalfa", label: "MyALFA", Icon: LayoutGrid },
        ].map(({ key, label, Icon }) => {
          const active = tab === key;
          const ouverts = compteOuverts(key);
          const retard = compteRetard(key);
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 h-10 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? "text-[#840040] border-[#840040] font-semibold"
                  : "text-slate-500 border-transparent hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              <span
                className={`min-w-[20px] h-[18px] px-1.5 rounded-full text-[10.5px] font-bold flex items-center justify-center ${
                  active ? "bg-[#840040] text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {ouverts}
              </span>
              {retard > 0 && (
                <span className="text-[10.5px] font-semibold text-red-600 bg-red-50 rounded-full px-2 py-0.5">
                  {retard} en retard
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Zone recherche & filtres statut */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un ticket, un utilisateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
          />
        </div>
        <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1">
          {[
            ["tous", "Tous", null],
            ["encours", "En cours", "bg-blue-600"],
            ["retard", "En retard", "bg-red-500"],
            ["clotures", "Clôturés", "bg-emerald-600"],
          ].map(([k, l, dot]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-medium transition-colors ${
                statusFilter === k
                  ? "bg-white text-slate-800 shadow-sm font-semibold"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
              {l}
              <span className="text-[10px] text-slate-400 font-semibold">{statusCounts[k]}</span>
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <button
          onClick={() => setShowArchives(!showArchives)}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 font-medium hover:bg-gray-50 transition-colors"
        >
          <Archive className="w-3.5 h-3.5" />
          {showArchives ? "Retour aux tickets" : "Archives"}
        </button>
      </div>

      {/* Filtre catégorie — uniquement pour l'onglet Informatique */}
      {isInfo && (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-xs text-slate-400 mr-0.5">Catégorie</span>
          <button
            onClick={() => setCategoryFilter("")}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
              !categoryFilter
                ? "bg-[#6e1a36] border-[#6e1a36] text-white"
                : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
            }`}
          >
            Toutes
          </button>
          {CATEGORIES.map((c) => {
            const actif = categoryFilter === c.value;
            return (
              <button
                key={c.value}
                onClick={() => setCategoryFilter(actif ? "" : c.value)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                  actif
                    ? "bg-[#6e1a36] border-[#6e1a36] text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: actif ? "#fff" : c.color }}
                />
                {c.label}
                <span className={actif ? "text-white/70" : "text-gray-400"}>
                  {categoryCounts[c.value] || 0}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Tableau */}
      <div className="mt-2 w-full overflow-x-auto">
        <table
          className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
          style={{ fontSize: "0.68rem" }}
        >
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Titre</th>
              {isInfo && (
                <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                  Catégorie
                </th>
              )}
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Description
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Utilisateur
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Statut</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Priorité
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Heure</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-right uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groupes.map((g) => {
              const dl = libelleJour(g.jour, todayStr, hierStr);
              const nOuverts = g.items.filter((t) => t.status !== "Terminé").length;
              return (
                <React.Fragment key={g.jour}>
                  <tr className="bg-slate-50">
                    <td colSpan={nbColonnes} className="px-3 py-1.5 border-b border-slate-100">
                      <div className="flex items-baseline gap-2.5 flex-wrap">
                        {dl.rel && (
                          <b className="text-[10px] font-bold text-[#840040] uppercase tracking-wide">
                            {dl.rel}
                          </b>
                        )}
                        <span className="text-[11px] font-semibold text-slate-700">{dl.long}</span>
                        <span className="text-[10px] text-slate-400">
                          {g.items.length} ticket{g.items.length > 1 ? "s" : ""}
                          {nOuverts > 0 ? ` · ${nOuverts} en cours` : ""}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {g.items.map((ticket) => {
                    const done = ticket.status === "Terminé";
                    const archived = ticket.status === "Archiver";
                    const late = estEnRetard(ticket, today);
                    return (
                      <tr
                        key={ticket.id}
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setShowOverlay(true);
                          setShowImageFullscreen(false);
                        }}
                        className={`cursor-pointer transition-colors ${
                          archived
                            ? "hover:bg-slate-50"
                            : done
                              ? "border-l-4 border-l-emerald-400 bg-emerald-50/30 hover:bg-emerald-50/60"
                              : late
                                ? "border-l-4 border-l-red-400 bg-red-50/40 hover:bg-red-50/70"
                                : "hover:bg-slate-50"
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <div className="tooltip-container">
                            {ticket.title
                              ? ticket.title.split(" ").slice(0, 3).join(" ") +
                                (ticket.title.split(" ").length > 3 ? "..." : "")
                              : " "}
                            {ticket.title && (
                              <div className="tooltip-text">{ticket.title}</div>
                            )}
                          </div>
                        </td>
                        {isInfo && (
                          <td className="px-3 py-2.5">
                            <CategorieChip value={ticket.categorie} />
                          </td>
                        )}
                        <td className="px-3 py-2.5">
                          <div className="tooltip-container">
                            {ticket.description
                              ? ticket.description.split(" ").slice(0, 5).join(" ") +
                                (ticket.description.split(" ").length > 5 ? "..." : "")
                              : " "}
                            {ticket.description && (
                              <div className="tooltip-text">{ticket.description}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-[#840040]/10 text-[#840040] text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                              {(ticket.nomUser || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-700 m-0 truncate">{ticket.nomUser}</p>
                              <p className="text-[10px] text-slate-400 m-0 truncate">{ticket.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <StatutPill ticket={ticket} today={today} />
                        </td>
                        <td className="px-3 py-2.5">
                          {ticket.priority && ticket.priority.trim() ? (
                            <span
                              className={
                                ticket.priority === "Urgent"
                                  ? "text-red-600 font-semibold"
                                  : "text-slate-500"
                              }
                            >
                              {ticket.priority}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">
                          {formatHeure(ticket.createdAt)}
                        </td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5">
                            {!done && !archived && (
                              <button
                                onClick={() => setConfirmCloture(ticket)}
                                className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full border border-emerald-200 bg-white text-emerald-700 text-[10.5px] font-semibold hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors whitespace-nowrap"
                              >
                                <Check className="w-2.5 h-2.5" />
                                Clôturer
                              </button>
                            )}
                            {done && (
                              <button
                                className="icon-button"
                                title="Archiver"
                                onClick={() => handleArchive(ticket)}
                              >
                                <Archive className="w-3.5 h-3.5 text-[#840040]" />
                              </button>
                            )}
                            {archived && (
                              <button
                                className="icon-button"
                                title="Restaurer"
                                onClick={() => handleDesArchive(ticket)}
                              >
                                <Archive className="w-3.5 h-3.5 text-[#840040]" />
                              </button>
                            )}
                            <button
                              className="icon-button"
                              title="Supprimer"
                              onClick={() => setConfirmDelete(ticket)}
                            >
                              <AiOutlineDelete size={15} color="#840040" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {listeFiltree.length === 0 && (
              <tr>
                <td colSpan={nbColonnes} className="py-16 text-center text-gray-400 text-xs">
                  {showArchives ? "Aucun ticket archivé" : "Aucun ticket ne correspond aux critères"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Overlays de confirmation — clôture (vert) / suppression (rouge) */}
      {confirmCloture && (
        <ConfirmClotureModal
          ticket={confirmCloture}
          onClose={() => setConfirmCloture(null)}
          onConfirm={() => handleValidate(confirmCloture)}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          ticket={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete)}
        />
      )}

      {/* Overlay détails ticket */}
      {showOverlay && selectedTicket && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayMS" ref={inoverlayRef}>
            <div className="details-view" style={{ width: "100%" }}>
              {/* Header */}
              <div className="flex items-start justify-between gap-4 px-8 pt-6 pb-4 border-b border-slate-100">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <StatutPill ticket={selectedTicket} today={today} />
                    {selectedTicket.priority && selectedTicket.priority.trim() && (
                      <span
                        className={`text-[11px] font-bold px-2 py-1 rounded-full border leading-none ${
                          selectedTicket.priority === "Urgent"
                            ? "bg-red-100 text-red-700 border-red-200"
                            : "bg-slate-100 text-slate-600 border-slate-200"
                        }`}
                      >
                        {selectedTicket.priority}
                      </span>
                    )}
                    <span className="text-[11px] font-medium px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 leading-none">
                      {estTypeInformatique(selectedTicket.type) ? "Informatique" : "MyALFA"}
                    </span>
                    {estTypeInformatique(selectedTicket.type) && (
                      <CategorieChip value={selectedTicket.categorie} />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 m-0 truncate">
                    {selectedTicket.title}
                  </h3>
                </div>
                <button
                  onClick={() => setShowOverlay(false)}
                  className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-8 py-5 space-y-4">
                {/* Description */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-xs font-semibold text-slate-500 mb-1.5 m-0">
                    Description
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap mt-1.5 mb-0">
                    {selectedTicket.description}
                  </p>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 p-3">
                    <User className="w-4 h-4 text-[#840040] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-400 font-medium m-0">
                        Utilisateur
                      </p>
                      <p className="text-xs font-semibold text-slate-700 truncate m-0">
                        {selectedTicket.nomUser}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 p-3">
                    <Mail className="w-4 h-4 text-[#840040] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-400 font-medium m-0">
                        Email
                      </p>
                      <p className="text-xs font-semibold text-slate-700 truncate m-0">
                        {selectedTicket.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 p-3 col-span-2">
                    <Calendar className="w-4 h-4 text-[#840040] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-400 font-medium m-0">
                        Date de création
                      </p>
                      <p className="text-xs font-semibold text-slate-700 m-0">
                        {new Date(selectedTicket.createdAt).toLocaleString(
                          "fr-FR"
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Document joint */}
                {selectedTicket.Document && (
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-2.5 m-0">
                      <FileText className="w-3.5 h-3.5" /> Document joint
                    </p>
                    {/\.(jpg|jpeg|png|webp)$/i.test(selectedTicket.Document) ? (
                      <div
                        onClick={() => setShowImageFullscreen(true)}
                        className="relative inline-block cursor-pointer group mt-2.5"
                      >
                        <img
                          src={selectedTicket.Document}
                          alt="Document joint"
                          className="max-w-full rounded-lg border border-slate-200 transition-opacity group-hover:opacity-90"
                          style={{ maxHeight: "300px" }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-full bg-white/90">
                            <Maximize2 className="w-4 h-4 text-slate-700" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <a
                        href={selectedTicket.Document}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-2.5 px-4 py-2 rounded-lg bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-semibold no-underline transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Télécharger le
                        document
                      </a>
                    )}
                  </div>
                )}

                {/* Commentaires */}
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-2.5 m-0">
                    <MessageSquare className="w-3.5 h-3.5" /> Commentaires
                  </p>
                  <div
                    className="space-y-3 pr-1"
                    style={{ maxHeight: "160px", overflowY: "auto" }}
                  >
                    {selectedTicket.CommentaireTicketing?.length > 0 ? (
                      selectedTicket.CommentaireTicketing.map((c, index) => (
                        <div key={index} className="flex gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#840040]/10 text-[#840040] text-[11px] font-bold flex items-center justify-center shrink-0">
                            {(c.nomComplet || "A").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-semibold text-slate-700">
                                {c.nomComplet || "Anonyme"}
                              </span>
                              {c.createdAt && (
                                <span className="text-[10px] text-slate-400">
                                  {new Date(c.createdAt).toLocaleString(
                                    "fr-FR"
                                  )}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-600 mt-0.5 m-0">
                              {c.commentaire}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-400 m-0">
                        Aucun commentaire pour le moment.
                      </p>
                    )}
                  </div>

                  <div className="flex items-end gap-2 mt-3 pt-3 border-t border-slate-100">
                    <textarea
                      value={commentaireInput}
                      onChange={(e) => setCommentaireInput(e.target.value)}
                      placeholder="Ajouter un commentaire..."
                      className="flex-1 rounded-lg border border-slate-200 p-2.5 text-xs text-slate-700 outline-none focus:border-[#840040]/40 transition-colors resize-none"
                      style={{ minHeight: "44px" }}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={isSendingComment}
                      className="shrink-0 h-[38px] px-4 rounded-lg bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                    >
                      {isSendingComment ? (
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                      {isSendingComment ? "Envoi..." : "Envoyer"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse plein écran du document joint */}
      {showImageFullscreen && selectedTicket?.Document && (
        <div
          onClick={() => setShowImageFullscreen(false)}
          className="fixed inset-0 flex items-center justify-center bg-black/80"
          style={{ zIndex: 100000 }}
        >
          <button
            onClick={() => setShowImageFullscreen(false)}
            className="absolute top-5 right-5 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedTicket.Document}
            alt="Document joint"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

export default TicketingAll;
